import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

/**
 * Order, transfer, and movement statuses are persisted Chinese values matched
 * in backend SQL — see outboundController.ts and inventoryTransferController.ts.
 * They are data, never UI text, so the raw value must survive every comparison,
 * filter, and request payload. Only the rendering is translated, keyed on the
 * raw value itself.
 *
 *   <Tag color={colourFor(order.status)}>{statusLabel(order.status)}</Tag>
 *
 * An unrecognised status passes through unchanged, so a new backend status
 * shows up as its Chinese name rather than disappearing.
 */
export const statusLabelFor = (
  t: TFunction,
  status: string | null | undefined
): string => (status ? t(status) : '')

/**
 * Must be a hook rather than a bare i18n.t call — a module-level t would not
 * re-render consumers when the language changes.
 */
export const useStatusLabel = () => {
  const { t } = useTranslation()
  return (status: string | null | undefined) => statusLabelFor(t, status)
}
